import { Auth0Client } from '@auth0/auth0-spa-js';
import { ColorScheme } from '../layout/colorScheme';
import { appInsights } from './appInsights';

export const isNativeApp = () => sessionStorage.getItem('app') === 'true';

type NativeMessage =
  | { type: 'theme'; color: string }
  | { type: 'hideOverlay' }
  | { type: 'plateScanner' }
  | { type: 'baseUri'; uri: string }
  | { type: 'LOGOUT' }
  | { type: 'ACTIVATE_MITID'; isBusinessUser: boolean; mklink?: string; uiLocales?: string }
  | {
      type: 'startLogin';
      authorizeUrl: string;
      preservedState: string | null;
      preservedStateKey: string;
      baseUrl: string;
    };

export const postMessage = (message: NativeMessage) => {
  if ((window as any).ReactNativeWebView) {
    (window as any).ReactNativeWebView.postMessage(JSON.stringify(message));
  } else if (window.nativeLayer) {
    appInsights.trackTrace({
      message: 'Native layer exists',
      properties: { hasNativeLayer: true }
    });
    appInsights.trackTrace({ message: 'Sending message to native layer ...' });
    window.nativeLayer.postMessage(message);
    appInsights.trackTrace({ message: 'Message sent to native layer.' });
  } else {
    appInsights.trackTrace({
      message: 'Native layer does not exist, simulating message',
      properties: { payload: message }
    });
  }
};

export const setPageColor = (pageColor: string = ColorScheme.DarkGreen) => {
  postMessage({ type: 'theme', color: pageColor });
};

export const webAppIsFullyLoaded = () => {
  postMessage({ type: 'hideOverlay' });
};

export const openLicensePlateScanner = () => {
  postMessage({ type: 'plateScanner' });
};

export const handleWrapperAppLogin = (url: string) => {
  const clientId = process.env.REACT_APP_AUTH_CLIENT_ID || '';
  const auth0TransactionKey = `a0.spajs.txs.${clientId}`;
  const transaction = sessionStorage.getItem(auth0TransactionKey);
  const baseUrl = window.location.origin;

  postMessage({
    type: 'startLogin',
    authorizeUrl: url,
    preservedState: transaction,
    preservedStateKey: auth0TransactionKey,
    baseUrl
  });
};

export const handleNativeAppLogin = async (auth0Client: Auth0Client) => {
  try {
    appInsights.trackTrace({
      message: 'Starting handleNativeAppLogin to initiate login via native app'
    });
    await auth0Client.loginWithRedirect({
      onRedirect: async (url: string) => {
        appInsights.trackTrace({
          message: `[Native Login] onRedirect called. Authorize URL generated by SDK: ${url}`
        });
        handleWrapperAppLogin(url);
        return Promise.resolve();
      }
    });
    appInsights.trackTrace({
      message: 'handleNativeAppLogin: loginWithRedirect with onRedirect setup complete.'
    });
    return true;
  } catch (error) {
    appInsights.trackException({ error: error as Error });
    return false;
  }
};

export const handleNativeAppLogout = () => {
  try {
    appInsights.trackTrace({
      message: 'Sending logout message to native app'
    });
    postMessage({ type: 'LOGOUT' });
    return true;
  } catch (error) {
    appInsights.trackException({ error: error as Error });
    return false;
  }
};

export const activateMitIdViaPostMessage = (
  isBusinessUser: boolean = false,
  mklink?: string,
  uiLocales?: string
): boolean => {
  try {
    appInsights.trackTrace({
      message: 'Sending MitID activation message to native app',
      properties: { isBusinessUser, hasMklink: !!mklink, uiLocales }
    });
    postMessage({ type: 'ACTIVATE_MITID', isBusinessUser, mklink, uiLocales });
    return true;
  } catch (error) {
    appInsights.trackException({ error: error as Error });
    return false;
  }
};
